import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  Text,
  useToast,
  Card,
  CardBody,
  Image,
  Input,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { useTranslation } from 'react-i18next';

interface CubeModel {
  id: string;
  brand: string;
  model: string;
  image?: string;
  price?: number;
  magneticLevel?: number;
  userCount?: number;
}

export const AdminCubes = () => {
  const [cubes, setCubes] = useState<CubeModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCube, setEditingCube] = useState<CubeModel | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    fetchCubes();
  }, []);

  const fetchCubes = async () => {
    try {
      const cubesRef = collection(db, 'cubeModels');
      const snapshot = await getDocs(cubesRef);
      const cubesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CubeModel[];
      setCubes(cubesData);
    } catch (error) {
      console.error('Error fetching cubes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load cubes',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (cube: CubeModel) => {
    try {
      if (cube.id) {
        // Update existing cube
        await updateDoc(doc(db, 'cubeModels', cube.id), {
          brand: cube.brand,
          model: cube.model,
          image: cube.image,
          price: cube.price,
          magneticLevel: cube.magneticLevel,
        });
      } else {
        // Add new cube
        await addDoc(collection(db, 'cubeModels'), {
          brand: cube.brand,
          model: cube.model,
          image: cube.image,
          price: cube.price,
          magneticLevel: cube.magneticLevel,
        });
      }
      await fetchCubes();
      onClose();
      toast({
        title: 'Success',
        description: cube.id ? 'Cube updated' : 'Cube added',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving cube:', error);
      toast({
        title: 'Error',
        description: 'Failed to save cube',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (cubeId: string) => {
    if (!window.confirm('Are you sure you want to delete this cube?')) return;
    
    try {
      await deleteDoc(doc(db, 'cubeModels', cubeId));
      await fetchCubes();
      toast({
        title: 'Success',
        description: 'Cube deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting cube:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete cube',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Card>
      <CardBody>
        <HStack justify="space-between" mb={4}>
          <Text fontSize="xl" fontWeight="bold">Cube Models</Text>
          <Button
            colorScheme="brand"
            onClick={() => {
              setEditingCube({
                id: '',
                brand: '',
                model: '',
              });
              onOpen();
            }}
          >
            Add New Cube
          </Button>
        </HStack>

        <Table>
          <Thead>
            <Tr>
              <Th>Image</Th>
              <Th>Brand</Th>
              <Th>Model</Th>
              <Th>Price</Th>
              <Th>Magnetic Level</Th>
              <Th>Users</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cubes.map((cube) => (
              <Tr key={cube.id}>
                <Td>
                  {cube.image && (
                    <Image
                      src={cube.image}
                      alt={`${cube.brand} ${cube.model}`}
                      boxSize="50px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                  )}
                </Td>
                <Td>{cube.brand}</Td>
                <Td>{cube.model}</Td>
                <Td>${cube.price}</Td>
                <Td>{cube.magneticLevel}</Td>
                <Td>{cube.userCount || 0}</Td>
                <Td>
                  <HStack>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() => {
                        setEditingCube(cube);
                        onOpen();
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleDelete(cube.id)}
                    >
                      Delete
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {/* Edit/Add Modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {editingCube?.id ? 'Edit Cube' : 'Add New Cube'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Brand</FormLabel>
                  <Input
                    value={editingCube?.brand || ''}
                    onChange={(e) => setEditingCube(prev => prev ? { ...prev, brand: e.target.value } : null)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Model</FormLabel>
                  <Input
                    value={editingCube?.model || ''}
                    onChange={(e) => setEditingCube(prev => prev ? { ...prev, model: e.target.value } : null)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Image URL</FormLabel>
                  <Input
                    value={editingCube?.image || ''}
                    onChange={(e) => setEditingCube(prev => prev ? { ...prev, image: e.target.value } : null)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Price ($)</FormLabel>
                  <Input
                    type="number"
                    value={editingCube?.price || ''}
                    onChange={(e) => setEditingCube(prev => prev ? { ...prev, price: parseFloat(e.target.value) } : null)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Magnetic Level (1-5)</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    max={5}
                    value={editingCube?.magneticLevel || ''}
                    onChange={(e) => setEditingCube(prev => prev ? { ...prev, magneticLevel: parseInt(e.target.value) } : null)}
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                onClick={() => editingCube && handleSave(editingCube)}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </CardBody>
    </Card>
  );
}; 